'use client';
import React, { useRef, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

const clients = [
  { name: 'Ftaxi', initial: 'F', color: 'bg-blue-600' },
  { name: 'Friendstrack', initial: 'Fr', color: 'bg-green-600' },
  { name: 'Maya', initial: 'M', color: 'bg-purple-600' },
  { name: 'Ktaxi', initial: 'K', color: 'bg-orange-500' },
  { name: 'Apple Call Taxi', initial: 'AC', color: 'bg-red-600' },
  { name: 'Rahul Travels', initial: 'RT', color: 'bg-teal-600' },
];

const tractionStats = [
  { value: '22+', label: 'Cities Operational', icon: 'MapPinIcon', desc: 'Actively powering mobility operations across Tamil Nadu, with expansion underway in Kerala, Karnataka, and Chhattisgarh.' },
  { value: '6', label: 'Partner Companies', icon: 'UsersIcon', desc: 'Trusted by established regional operators and transport associations serving thousands of customers every day.' },
  { value: '5,500+', label: 'Trips Every Day', icon: 'TruckIcon', desc: 'Processing live ride bookings daily across multiple cities, proving platform stability, operational efficiency, and market adoption.' },
  { value: 'Unified', label: 'Fleet Booking Network', icon: 'BuildingOffice2Icon', desc: 'A single platform where fleet owners can register, manage vehicles, receive bookings, and grow their business through a shared mobility ecosystem.' },
  { value: '3M+', label: 'Successful Trips Completed', icon: 'CheckCircleIcon', desc: 'Thousands of customers already rely on our platform for their daily mobility needs.' },
  { value: 'Multi-State', label: 'Expansion in Progress', icon: 'GlobeAltIcon', desc: 'Successful launches, active onboarding, and strategic partnerships are accelerating our footprint beyond South India.' },
];

const states = ['Tamilnadu', 'Kerala', 'Chhattisgarh'];

function FlipCard({ stat, index }: { stat: typeof tractionStats[0]; index: number }) {
  return (
    <div
      className="flip-card animate-on-scroll"
      style={{ animationDelay: `${index * 0.08}s`, '--stagger': index } as React.CSSProperties}
    >
      <div className="flip-card-inner">
        {/* Front */}
        <div className="flip-card-front bg-[#FFFFFF] rounded-2xl border border-[rgba(15,23,42,0.07)] p-7 flex flex-col justify-between">
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
            style={{ background: 'linear-gradient(135deg, rgba(0, 102, 255,0.12), rgba(7, 93, 223,0.08))', border: '1px solid rgba(0, 102, 255,0.15)' }}
          >
            <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-[#0066FF]" />
          </div>
          <div>
            <div className="font-display text-4xl font-semibold text-[#0066FF] mb-2 leading-none">{stat.value}</div>
            <div className="font-semibold text-[#0F172A] text-base leading-snug">{stat.label}</div>
          </div>
          <div className="mt-4 flex items-center gap-1.5 text-[11px] text-[#64748B] font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF] opacity-60" />
            Hover to learn more
          </div>
        </div>

        {/* Back */}
        <div
          className="flip-card-back rounded-2xl p-7 flex flex-col justify-between"
          style={{ background: 'linear-gradient(135deg, #0066FF 0%, #075DDF 100%)' }}
        >
          <div
            className="w-11 h-11 rounded-xl flex items-center justify-center mb-4 bg-white/15 border border-white/25"
          >
            <Icon name={stat.icon as Parameters<typeof Icon>[0]['name']} size={20} className="text-white" />
          </div>
          <div>
            <div className="font-display text-3xl font-semibold text-white mb-2 leading-none">{stat.value}</div>
            <div className="font-semibold text-white/90 text-base mb-3 leading-snug">{stat.label}</div>
            <p className="text-sm text-white/75 leading-relaxed">{stat.desc}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientsSection() {
  const tractionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.animate-on-scroll').forEach((el) => el.classList.add('is-visible'));
          }
        });
      },
      { threshold: 0.05 }
    );
    if (tractionRef.current) observer.observe(tractionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Traction Section */}
      <section ref={tractionRef} className="py-20 bg-white relative overflow-hidden">
        {/* Subtle circuit bg */}
        <div className="absolute inset-0 circuit-bg opacity-30 pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-14 animate-on-scroll">
            <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-widest mb-2">Traction</p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold text-[#0F172A] tracking-tight mb-4">
              Built in the Real World.{' '}
              <span className="italic font-light text-[#0066FF]">Proven at Scale.</span>
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto text-base leading-relaxed">
              Movio is not an idea in development—it is a live, revenue-generating platform already powering local mobility businesses across multiple cities.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {tractionStats.map((stat, i) => (
              <FlipCard key={stat.label} stat={stat} index={i} />
            ))}
          </div>

          {/* Validated Business Model note */}
          <div className="animate-on-scroll bg-[#FFFFFF] rounded-2xl border border-[rgba(0, 102, 255,0.2)] p-8 text-center hover-lift transition-all duration-300">
            <div className="flex items-center justify-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(0, 102, 255,0.12), rgba(7, 93, 223,0.08))' }}>
                <Icon name="ChartBarIcon" size={16} className="text-[#0066FF]" />
              </div>
              <p className="text-[#0F172A] font-semibold text-lg">Validated Business Model</p>
            </div>
            <p className="text-[#475569] text-base leading-relaxed max-w-3xl mx-auto">
              Operators pay upfront platform fees, recurring SaaS subscriptions, and performance-linked revenue sharing—creating predictable, scalable, and high-margin growth. Built, tested, and optimized in Tier 2, Tier 3, and Tier 4 markets where traditional aggregators struggle to penetrate.
            </p>
          </div>
        </div>
      </section>

      {/* Trusted Partners */}
      <section className="py-16 bg-[#FFFFFF] border-t border-[rgba(15,23,42,0.08)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-semibold text-[#0066FF] uppercase tracking-widest mb-2">Trusted Partners</p>
            <h2 className="text-3xl font-bold text-[#0F172A] mb-3">Companies Growing with Us</h2>
            <p className="text-[#64748B] max-w-xl mx-auto text-base">
              Powering taxi and mobility businesses across{' '}
              {states.map((s, i) => (
                <span key={s}>
                  <span className="font-medium text-[#475569]">{s}</span>
                  {i < states.length - 1 ? ', ' : ''}
                </span>
              ))}{' '}
              and <span className="font-semibold text-[#0066FF]">25+ cities</span> across India.
            </p>
          </div>

          {/* Client Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {clients.map((client, i) => (
              <div
                key={client.name}
                className="client-card flex flex-col items-center justify-center bg-white rounded-xl shadow-sm border border-[rgba(15,23,42,0.08)] py-6 px-3 hover:shadow-md transition-all duration-300"
                style={{ animationDelay: `${i * 0.06}s` }}
              >
                <div className={`w-12 h-12 rounded-full ${client.color} flex items-center justify-center text-white font-bold text-lg mb-3 transition-transform duration-300 group-hover:scale-110`}>
                  {client.initial}
                </div>
                <span className="text-sm font-semibold text-[#0F172A] text-center leading-tight">{client.name}</span>
              </div>
            ))}
          </div>

          {/* Stats Row */}
          <div className="flex flex-wrap justify-center gap-8 mt-6">
            {[
              { value: '6+', label: 'Partner Companies' },
              { value: '3', label: 'States Covered' },
              { value: '25+', label: 'Cities Served' },
            ].map((s) => (
              <div key={s.label} className="text-center hover-lift transition-all duration-300 px-4">
                <p className="text-2xl font-bold text-[#0066FF]">{s.value}</p>
                <p className="text-sm text-[#64748B] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}